/* -------------------------- Design imports start -------------------------- */
import { LoadingButton } from '@mui/lab'
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography } from '@mui/material'
import React, { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CategoryDrawer from './CategoryDrawer'
import PropertyDrawer from './PropertyDrawer'
import GetConstant from './utils/constants'
// Removed old callback-based functions - now using React Query hooks
import FileInput from '../../components/inputs/FileInput'
import InputFileList from '../../components/widgets/InputFileList'
import { FilesDrawer } from '../Filesystem'
import { Form, type FormFieldType, type FormObject } from '../Form'
import SupplierDrawer from '../Supplier/SupplierDrawer'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import LogTool from '../../logger/logTools'
import { type FileInfo, type FileUploadStatus } from '../../utils/types'
import { missingRequiredFormFields } from '../../utils/functions'
import MultilevelDrawer from '../../components/layout/MultilevelDrawer'
import VersionSubmit from '../Version/components/VersionSubmit'
import { type Version } from '../Version/utils/types'
import Button from '../../components/Button'
import { type ArticleFormInput } from './utils/types'
// React Query imports
import { useSuppliers } from '../Supplier/hooks/useSupplierQueries'
import {
  useBrands,
  useAllCategories,
  useAllProperties,
  useAllMaterials,
} from './hooks/useArticleQueries'
import { useCreateCategoryOrProperty } from './hooks/useArticleMutations'
import {
  deleteFileFromUrl,
  uploadFileToUrl,
  fetchFileList as fetchFileListApi,
} from '../Filesystem/api/fileApi'
import { deriveFilesUrl } from '../Filesystem/utils/filesHelper'
import useFetchExistingFiles from '../../hooks/useFetchExistingFiles'
import { useUserContext } from '../../utils/context'
import ArticleForm from './ArticleForm'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: ArticleFormInput | undefined
    setState: React.Dispatch<React.SetStateAction<ArticleFormInput | undefined>>
  }
  onClose?: () => void
  onCancel?: () => void
  onCreateConfirm: (a: ArticleFormInput | undefined) => Promise<string | FileInfo[] | void>
  enableEditMode?: boolean
  onEditConfirm?: (input: any) => Promise<string | void>
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function ArticleDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    formState,
    onClose = () => null,
    onCreateConfirm = () => null,
    enableEditMode = false,
    onEditConfirm = () => null,
  } = props
  const log = new LogTool({ context: 'ArticleDrawer', enable: true, logLevel: 'debug' })
  const { t } = useTranslation(['item'])
  const { user } = useUserContext()
  const isGuestUser = user?.isGuestUser ?? false

  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [prevOpen, setPrevOpen] = useState<boolean>(false)
  // this modal needs to control its waiting state itself as it implements custom modal actions
  const [waitingOnAction, setWaiting] = useState(false)
  const [openCategoryDrawer, setOpenCategoryDrawer] = useState(false)
  const [openPropertyDrawer, setOpenPropertyDrawer] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  // we have to support both types ArticleFile and File as we have to deal with File objects before uploading the files, but with ArticleFile objects after fetching the files
  const [inputFiles, setInputFiles] = useState<any[]>([])
  // Separate state for already uploaded files (displayed in Ant Design table)
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([])
  const [openFilesDrawer, setOpenFilesDrawer] = useState(false)
  /* ----------------------------- Data states end ---------------------------- */

  /* ---------------------------- React Query Hooks --------------------------- */

  /* ------------------------------ Effects start ----------------------------- */

  // Fetch existing files when drawer opens, formState changes, or FilesDrawer opens
  useFetchExistingFiles({
    open,
    formState,
    openFilesDrawer,
    setUploadedFiles,
    logContext: 'article',
  })

  /* ------------------------------ Effects end ----------------------------- */

  /* ------------------------ Utility functions start ----------------------- */
  /**
   * Check if all required FormFields are filled in correctly.
   * @returns
   */
  const isFormValid = (): boolean => {
    // Get required fields: Material and Number
    const requiredFields: FormFieldType[] = [
      {
        key: 'material',
        required: true,
        validation: true,
        type: 'select-search',
        label: t('item:content.label.material'),
      },
      {
        key: 'number',
        required: true,
        validation: true,
        type: 'text',
        label: t('common:content.label.articleNumber'),
      },
    ]
    return !missingRequiredFormFields(requiredFields, formState.state)
  }

  /**
   * Setter for the uploadStatus property on a FileInfo object within this component.
   * Do not use this function if you want to update multiple files at once as the component is rerendered after every status update!
   * @param file the file on which the uploadStatus should be updated.
   * @param status the updated uploadStatus.
   */
  function setFileUploadStatus(file: FileInfo, status: FileUploadStatus): void {
    setInputFiles((prev: FileInfo[]) =>
      prev.map((listedFile: FileInfo) => {
        if (listedFile.name === file.name) {
          // add an upload-status of success to the file info object or overwrite it with the provided status
          return Object.assign(file, { uploadStatus: status })
        }
        return listedFile
      })
    )
  }
  /**
   * Wrapper for the uploadFile function that specifies the onSuccess and onError callbacks.
   * This function simply allows us to upload files using the same behavior at multiple places
   * within this component without rewriting the callback functions all the time.
   * @param file the file to upload.
   * @param url the url to upload the file to.
   */
  async function uploadArticleFile(file: FileInfo, url: string): Promise<void> {
    try {
      const uploadedFile = await uploadFileToUrl(url, file as File)
      setInputFiles((prev: FileInfo[]) =>
        prev.map((listedFile: FileInfo) => {
          if (listedFile.name === file.name) {
            return { ...uploadedFile, uploadStatus: 'success' }
          }
          return listedFile
        })
      )
    } catch (error) {
      log.error('Error uploading article file', error)
      setFileUploadStatus(file, 'uploadError')
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false): void => {
    // reset form state
    setWaiting(false)
    formState.setState(undefined)
    setInputFiles([])
    setUploadedFiles([])

    setOpen(false)
    setPrevOpen(false)
    !preventOnClose && onClose()
  }

  const handleCreateConfirm = async (): Promise<void> => {
    // visual feedback
    setWaiting(true)

    // set loading state on all files to loading
    if (inputFiles.length > 0) {
      setInputFiles((prev: FileInfo[]) =>
        prev.map(file => Object.assign(file, { uploadStatus: 'loading' }))
      )
    }
    // posting request to server and waiting on response
    const articleFilesUrl: string | FileInfo[] | void | null = await onCreateConfirm(
      formState.state ? { ...formState.state } : undefined
    )
    // check if the user specified files to upload !
    if (inputFiles.length > 0 && articleFilesUrl) {
      // upload the files one by one for a more detailed feedback if something goes wrong,
      // and to make use parallel uploading to decrease upload time
      for (const file of inputFiles) {
        if (typeof articleFilesUrl === 'string') {
          await uploadArticleFile(file, articleFilesUrl)
        } else if (
          Array.isArray(articleFilesUrl) &&
          articleFilesUrl.length > 0 &&
          articleFilesUrl[0].uploadUrl
        ) {
          await uploadArticleFile(file, articleFilesUrl[0].uploadUrl)
        }
        // response.articleFilesUrl cannot be undefined here as the server created the article successfully and therefore created an article files url

        // store the articleFilesUrl in the inputFiles state to have it available in case something goes wrong
        /* setInputFiles((prev: FileInfo[]) => prev.map(
          file => Object.assign(file, {uploadUrl: response.articleFilesUrl})
        )) */
      }

      // Refresh uploaded files list after successful upload
      // Note: After create, formState.state.self might be updated by onCreateConfirm
      // We need to wait a bit for the state to update, or use the articleFilesUrl to derive the files URL
      if (formState.state?.self) {
        try {
          const filesUrl = deriveFilesUrl(formState.state)
          if (filesUrl) {
            const fileResponse = await fetchFileListApi(filesUrl)
            setUploadedFiles(fileResponse.results)
          }
        } catch (error) {
          log.error('Error refreshing uploaded files after create', error)
        }
      } else if (typeof articleFilesUrl === 'string') {
        // If formState.state.self is not yet available, use articleFilesUrl to fetch files
        try {
          const fileResponse = await fetchFileListApi(articleFilesUrl)
          setUploadedFiles(fileResponse.results)
        } catch (error) {
          log.error('Error refreshing uploaded files after create using articleFilesUrl', error)
        }
      }
    }
    setWaiting(false)
    handleClose()
  }

  const handleEditConfirm = async (): Promise<void> => {
    // posting request to server and waiting on response
    log.debug('formState.state in editConfirm', formState.state)
    const articleFilesUrl = await onEditConfirm(formState.state)
    if (inputFiles.length > 0 && articleFilesUrl) {
      // upload the files one by one for a more detailed feedback if something goes wrong,
      // and to make use parallel uploading to decrease upload time
      for (const file of inputFiles) {
        await uploadArticleFile(file, articleFilesUrl)
        // response.articleFilesUrl cannot be undefined here as the server created the article successfully and therefore created an article files url

        // store the articleFilesUrl in the inputFiles state to have it available in case something goes wrong
        setInputFiles((prev: FileInfo[]) =>
          prev.map(file => Object.assign(file, { uploadUrl: articleFilesUrl }))
        )
      }
    }

    // Refresh uploaded files list after successful upload
    if (formState.state?.self) {
      try {
        const filesUrl = deriveFilesUrl(formState.state)
        if (filesUrl) {
          const fileResponse = await fetchFileListApi(filesUrl)
          setUploadedFiles(fileResponse.results)
        }
      } catch (error) {
        log.error('Error refreshing uploaded files after edit', error)
      }
    }

    handleClose()
  }

  log.debug('formState', formState.state)
  const handleReUploadFile = async (file: FileInfo, url: string): Promise<void> => {
    // set the uploadStatus of the current file to loading
    setFileUploadStatus(file, 'loading')
    // retry to upload the file
    await uploadArticleFile(file, url)
  }

  const handleRemoveFileFromList = (file: FileInfo): void => {
    // update rendered files list
    setInputFiles(prevFileList => {
      // get the location of the file to remove within the files list
      const fileIndex = prevFileList.findIndex(listedFile => listedFile.name === file.name)
      // remove the file from the list. Note that this does not work on the prevFileList directly
      const updatedFileList = [...prevFileList]
      updatedFileList.splice(fileIndex, 1)

      // return the files list where the file is removed
      return updatedFileList
    })
  }

  const handleDeleteFile = async (file: FileInfo): Promise<void> => {
    // try to delete the file
    try {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      const deleteUrl = formState.state?.self + 'files/' + file.self!.split('/').slice(-2)[0] + '/'
      await deleteFileFromUrl(deleteUrl)
      // Remove from uploadedFiles
      setUploadedFiles(prev => prev.filter(f => f.self !== file.self))
    } catch (error) {
      log.error('Error deleting article file', error)
      throw error // Re-throw so UploadedFilesTable can handle it
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */

  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  // initialize select options every time the modal opens
  // Note: do not move these state setting operations into a useEffect as that leads to unecessary re-rendering the component

  if (open && !prevOpen) {
    setPrevOpen(true)
    // when this modal is opened in edit mode, the article might have files that we need to display
    if (enableEditMode && formState.state && Object.hasOwn(formState.state, 'files')) {
      void (async () => {
        try {
          const filesUrl =
            typeof formState.state?.files[0] !== 'string' && formState.state?.files[0]?.self
              ? formState.state?.files[0]?.self
              : ''
          if (filesUrl) {
            const fileResponse = await fetchFileListApi(filesUrl)
            setInputFiles(fileResponse.results)
          }
        } catch (error) {
          log.error('Error fetching article files', error)
        }
      })()
    }
  }

  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('formState.state ->', formState.state, 'inputFiles ->', inputFiles)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <MultilevelDrawer
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      size="big"
      customHeader={
        <Typography variant="h6" noWrap>
          {enableEditMode
            ? t('item:content.heading.editArticle')
            : t('item:content.heading.createNewArticle')}
        </Typography>
      }
      customActions={
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <Button
            id="cancelButton"
            onClick={() => setOpen(false)}
            color="inherit"
            variant="contained"
            disabled={waitingOnAction}
            sx={{ mr: 1, textTransform: 'none' }}
          >
            {t('common:interaction.button.cancel')}
          </Button>
          <LoadingButton
            id="createButton"
            variant="contained"
            sx={{ textTransform: 'none', marginLeft: '10px', color: 'whitesmoke' }}
            disabled={!isFormValid()}
            loading={waitingOnAction}
            onClick={() => {
              void (async () => {
                if (enableEditMode) {
                  await handleEditConfirm()
                } else {
                  await handleCreateConfirm()
                }
              })()
            }}
          >
            {enableEditMode
              ? t('interaction.button.updateItem')
              : t('interaction.button.createItem')}
          </LoadingButton>
        </Box>
      }
    >
      <ArticleForm
        formState={{
          state: formState.state,
          setState: formState.setState,
        }}
        inputFiles={{
          state: inputFiles,
          setState: setInputFiles,
        }}
        uploadedFiles={{
          state: uploadedFiles,
          setState: setUploadedFiles,
        }}
      />
    </MultilevelDrawer>
  )
}
